import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Linkedin, Twitter, Instagram, Mail } from 'lucide-react';
import { companyInfo } from '../data/mock';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' }
  ];

  const socialLinks = [
    { name: 'LinkedIn', href: companyInfo.social.linkedin, icon: Linkedin },
    { name: 'Twitter', href: companyInfo.social.twitter, icon: Twitter },
    { name: 'Instagram', href: companyInfo.social.instagram, icon: Instagram }
  ];

  return (
    <footer className="bg-gray-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center">
              {!logoError ? (
                <img
                  src="/assets/images/gensurf-logo.svg"
                  alt="Gensurf Logo"
                  className="h-8 w-auto"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <span className="text-2xl font-league-spartan font-light text-primary tracking-tight">
                  {companyInfo.name}
                </span>
              )}
            </Link>
            <p className="text-gray-600 font-league-spartan text-sm leading-relaxed">
              {companyInfo.mission}
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-500 hover:text-primary hover:bg-primary/5 transition-all duration-200 shadow-sm"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-gray-900 font-league-spartan font-medium mb-6">Navigation</h3>
            <ul className="space-y-4">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-gray-600 font-league-spartan hover:text-primary transition-colors duration-200 text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-gray-900 font-league-spartan font-medium mb-6">Contact</h3>
            <div className="space-y-4 text-sm">
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <a 
                  href={`mailto:${companyInfo.contact.email}`}
                  className="text-gray-600 font-league-spartan hover:text-primary transition-colors"
                >
                  {companyInfo.contact.email}
                </a>
              </div>
              <div className="text-gray-600 font-league-spartan">
                {companyInfo.contact.phone}
              </div>
              <div className="text-gray-600 font-league-spartan">
                {companyInfo.contact.address}
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-gray-900 font-league-spartan font-medium mb-6">Stay Updated</h3>
            <p className="text-gray-600 font-league-spartan text-sm mb-4">
              Subscribe to our newsletter for the latest AI insights and updates.
            </p>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white border-gray-200 focus:border-primary focus:ring-primary"
                required
              />
              <Button 
                type="submit"
                className="w-full"
              >
                {isSubscribed ? '✓ Subscribed!' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>

        {/* Bottom Border */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-gray-500 font-league-spartan text-sm">
              © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy-policy" className="text-gray-500 font-league-spartan hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-gray-500 font-league-spartan hover:text-primary transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;